import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Set on <html> while a bar is mounted: the bar's rendered height, safe-area
 * padding included, so floating controls can sit above it.
 */
export const MOBILE_ACTION_BAR_HEIGHT_VAR = "--mobile-action-bar-height";

interface MobileActionBarAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "destructive";
  disabled?: boolean;
}

interface MobileActionBarProps {
  actions: MobileActionBarAction[];
  className?: string;
}

export function MobileActionBar({ actions, className }: MobileActionBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const hasActions = actions.length > 0;

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const rootStyle = document.documentElement.style;
    const update = () => rootStyle.setProperty(MOBILE_ACTION_BAR_HEIGHT_VAR, `${bar.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(bar, { box: "border-box" });
    return () => {
      observer.disconnect();
      rootStyle.removeProperty(MOBILE_ACTION_BAR_HEIGHT_VAR);
    };
  }, [hasActions]);

  if (!hasActions) return null;

  return (
    // bg-background/95 emits no CSS (opacity on a var() color), so the bar is see-through and
    // the page blurs behind the icons; dark mode gets a solid surface and no gold tap flash.
    <div ref={barRef} className={cn(
      "fixed bottom-0 left-0 right-0 z-50 lg:hidden",
      "border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 dark:bg-background",
      "px-4 py-2 safe-area-pb",
      className
    )}>
      <div className="flex items-center justify-around gap-1">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className={cn(
              "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors min-w-0 flex-1",
              "active:bg-accent dark:active:bg-white/10 dark:[-webkit-tap-highlight-color:transparent]",
              action.variant === "destructive"
                ? "text-destructive"
                : "text-muted-foreground hover:text-foreground",
              action.disabled && "opacity-50 pointer-events-none"
            )}
          >
            {action.icon}
            <span className="truncate">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
